import { useEffect, useRef, useState } from "react";
import { ArticleData } from "@/lib/types";
import { Download, Calendar, FileText } from "lucide-react";
import RieeLogo from "@/components/RieeLogo";

interface ArticleHeroProps {
  article: ArticleData;
}

const categoryConfig: Record<string, { badge: string; glow: string; label: string }> = {
  ESTUDIOS: { badge: "bg-[var(--riee-purple)] text-white", glow: "rgba(107,58,142,0.35)", label: "ESTUDIOS" },
  REFLEXIONES: { badge: "bg-[var(--riee-gold)] text-gray-900", glow: "rgba(200,169,81,0.25)", label: "REFLEXIONES" },
  "RESEÑAS": { badge: "bg-white/20 text-white border border-white/40", glow: "rgba(255,255,255,0.1)", label: "RESEÑAS" },
};

function AnimatedKpi({ value, label, unit }: { value: string; label: string; unit?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.4 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="hero-stat flex flex-col items-center"
      style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(16px)", transition: "opacity 0.6s ease, transform 0.6s ease" }}
    >
      <div className="text-[var(--riee-gold)] font-bold text-xl leading-tight font-mono">{value}</div>
      <div className="text-white/55 text-[10px] mt-0.5 uppercase tracking-wide">{label}</div>
      {unit && <div className="text-white/35 text-[10px]">{unit}</div>}
    </div>
  );
}

export default function ArticleHero({ article }: ArticleHeroProps) {
  const cc = categoryConfig[article.category] ?? categoryConfig["ESTUDIOS"];
  const [vis, setVis] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVis(true), 60); return () => clearTimeout(t); }, []);

  return (
    <section
      className="relative min-h-[80vh] flex flex-col justify-end pb-12 overflow-hidden"
      style={{ background: "linear-gradient(135deg, #0A0F1A 0%, #12082E 60%, #0D1520 100%)" }}
    >
      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(200,169,81,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(200,169,81,0.03) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      {/* Glow orbs */}
      <div
        className="absolute -top-20 right-0 w-[480px] h-[480px] rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${cc.glow} 0%, transparent 70%)`,
          transform: "translate(25%, -25%)",
          animation: "pulse-slow 8s ease-in-out infinite",
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-72 h-72 rounded-full pointer-events-none"
        style={{
          background: "radial-gradient(circle, rgba(200,169,81,0.1) 0%, transparent 70%)",
          transform: "translate(-30%, 30%)",
          animation: "pulse-slow 11s ease-in-out infinite 3s",
        }}
      />

      {/* Header top strip: RIEE logo */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 py-5 z-10"
        style={{ opacity: vis ? 1 : 0, transition: "opacity 0.7s ease 0.1s" }}
      >
        <RieeLogo height={38} />
        <div className="flex items-center gap-2">
          <div className="h-px w-10 bg-[var(--riee-gold)] opacity-50" />
          <span className="text-white/30 text-xs font-mono uppercase tracking-widest">
            Vol. 26, Núm. 1 · 2026
          </span>
        </div>
      </div>

      {/* Main content */}
      <div
        className="relative z-10 max-w-5xl mx-auto px-6 pt-32"
        style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(24px)", transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s" }}
      >
        {/* Label strip */}
        <div className="flex items-center gap-3 mb-5">
          <div className="h-px w-8 bg-[var(--riee-gold)] opacity-60" />
          <span className={`inline-flex items-center px-4 py-1 rounded-full text-xs font-bold uppercase tracking-widest ${cc.badge}`}>
            {article.category}
          </span>
          <div className="h-px w-8 bg-[var(--riee-gold)] opacity-60" />
        </div>

        {/* Title */}
        <h1
          className="text-white text-3xl md:text-5xl font-bold leading-tight mb-7 max-w-3xl"
          style={{ fontFamily: "var(--font-serif)", textShadow: "0 2px 40px rgba(107,58,142,0.25)" }}
        >
          {article.title}
        </h1>

        {/* Authors */}
        <div className="flex flex-wrap gap-x-8 gap-y-3 mb-8">
          {article.authors.map((a) => (
            <div key={a.name} className="flex flex-col">
              <span className="text-white font-semibold text-sm">{a.name}</span>
              <span className="text-white/50 text-xs">{a.institution}</span>
              {a.country && (
                <span className="text-[var(--riee-gold)] text-xs font-mono opacity-70">{a.country}</span>
              )}
            </div>
          ))}
        </div>

        {/* RIEE divider */}
        <div className="riee-divider my-6 max-w-sm">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <rect x="5" y="0" width="2" height="12" fill="var(--riee-gold)" opacity="0.7" />
            <rect x="0" y="5" width="12" height="2" fill="var(--riee-gold)" opacity="0.7" />
          </svg>
        </div>

        {/* KPI stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {article.keyFindings.map((kf, i) => (
            <div
              key={kf.label}
              style={{ transitionDelay: `${0.3 + i * 0.08}s` }}
            >
              <AnimatedKpi value={kf.value} label={kf.label} unit={kf.unit} />
            </div>
          ))}
        </div>

        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-white/45 text-xs font-mono border-t border-white/10 pt-5">
          <span className="flex items-center gap-1.5">
            <FileText size={12} className="text-[var(--riee-gold)] opacity-70" />
            DOI: {article.doi}
          </span>
          <span className="flex items-center gap-1.5">
            <Calendar size={12} className="text-[var(--riee-gold)] opacity-70" />
            Pp. {article.pagesStart}–{article.pagesEnd}
          </span>
          <span>Recibido: {article.receivedDate}</span>
          <span>Aceptado: {article.acceptedDate}</span>

          {article.pdfUrl && (
            <a
              href={article.pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-download ml-auto"
            >
              <Download size={14} />
              Ver en RIEE
            </a>
          )}
        </div>
      </div>
    </section>
  );
}
